/**
 * Wonda's pixel cat as the homepage hero draws it: one platform mark per lattice
 * cell the logo's cat covers, the cells near its pupils in the accent. Shared by
 * the PixelCat part and the OG images, so it is plain data with no React.
 */
import {
  LINKEDIN_PATH,
  REDDIT_PATH,
  SALES_NAV_PATH,
  X_PATH,
} from "@/landing/lib/hero-marks";

/**
 * WONDA_CAT_PATH rasterised onto a 54-cell lattice (4x supersampled, 42%
 * coverage): the cells the homepage hero, the PixelCat part and the OG images
 * all draw. Regenerate from the path if the cat changes.
 */
export const CAT_ROWS = [
  "......................................................",
  "......................................................",
  ".....................####................#####........",
  "....................#####................######.......",
  "....................#######......#.....#########......",
  "...................########......#.....#########......",
  "..................###....################.....###.....",
  "..................###....################.....###.....",
  "..................###......######.#####.......###.....",
  "..................##.......##................####.....",
  ".................###........#...............#####.....",
  ".................###......########....###########.....",
  ".................###......########....########.###....",
  "..............#####......##......##..##......######...",
  "..............#####......##......##..##......######...",
  "..........########......###..##..##..##..##..#######..",
  "..........#######......####..##..##..##..###.#######..",
  ".......########........####.####.##..##.####.#######..",
  ".......#######.........####.####.##..##.####.#######..",
  ".....#####.............####..##..##..##..##..#######..",
  "....######.............####..##..##..##...#..#######..",
  "....###.................###......##..##......#######..",
  "..###....................#########....#########..###..",
  "..###.....................########....########...###..",
  "..##......................##.................#..###...",
  "..##......................##.................#..###...",
  ".##............................................###....",
  ".##............................................###....",
  "##............................................###.....",
  "##............................................##......",
  "##...........................................###......",
  "##...........................................##.......",
  "##..........................................##........",
  "##.....................######................##.......",
  "##.....................######................###......",
  "##.................##.#########..............###......",
  "##................###.#########..............###......",
  "##................#####......###.............###......",
  "##...............######......###.............###......",
  "##..............###..#########.##...........##........",
  "##..............###.##########.##...........##........",
  ".##..............#####.########.##.........###........",
  ".##...............####.########.##.........###........",
  "..###.............###........##.##......####..........",
  "..#####............#........###.############..........",
  "...####.....................###.###########...........",
  "....#######...............#####.###########...........",
  ".....######...............#####.########..............",
  ".......#####################..........................",
  ".......#####################..........................",
  "...........###############............................",
  "...........###############............................",
  "......................................................",
  "......................................................",
];

/** Mark order matches the hero's `marks` array. */
export const CAT_MARK_PATHS = [
  LINKEDIN_PATH,
  SALES_NAV_PATH,
  X_PATH,
  REDDIT_PATH,
] as const;

// The pupils, in lattice cells; marks within 2.7 cells of them are accented.
export const CAT_PUPILS: [number, number][] = [
  [30, 18],
  [42, 18],
];

export type CatCell = { x: number; y: number; mark: number; hot: boolean };

/** Every filled cell with its mark, picked by the hero's seeded generator. */
export const CAT_CELLS: CatCell[] = (() => {
  let seed = 5;
  const rand = () => {
    seed = (seed * 16807) % 2147483647;
    return seed / 2147483647;
  };
  const cells: CatCell[] = [];
  CAT_ROWS.forEach((row, y) => {
    for (let x = 0; x < row.length; x++) {
      if (row[x] !== "#") continue;
      cells.push({
        x,
        y,
        mark: Math.floor(rand() * CAT_MARK_PATHS.length),
        hot: CAT_PUPILS.some(
          ([px, py]) => Math.hypot(x + 0.5 - px, y + 0.5 - py) < 2.7,
        ),
      });
    }
  });
  return cells;
})();

/** The cat's bounds in cells, for a tight viewBox. */
export const CAT_VIEWBOX = "0 2 53 50";

/** Each mark fills 86% of its cell, centred, like the hero. */
export function catMarkTransform(cell: CatCell): string {
  return `translate(${cell.x + 0.07} ${cell.y + 0.07}) scale(${0.86 / 24})`;
}

/** The cat as a standalone SVG string (OG images and anywhere without React). */
export function buildPixelCatSvg({
  ink,
  accent,
}: {
  ink: string;
  accent: string;
}): string {
  const defs = CAT_MARK_PATHS.map(
    (path, index) => `<path id="m${index}" d="${path}" fill-rule="evenodd"/>`,
  ).join("");
  const uses = (hot: boolean) =>
    CAT_CELLS.filter((cell) => cell.hot === hot)
      .map(
        (cell) =>
          `<use href="#m${cell.mark}" transform="${catMarkTransform(cell)}"/>`,
      )
      .join("");
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${CAT_VIEWBOX}"><defs>${defs}</defs><g fill="${ink}">${uses(false)}</g><g fill="${accent}">${uses(true)}</g></svg>`;
}
